import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PublicTables, Checkin, Venue } from "@/lib/api";
import { toast } from "sonner";
import { Spade, Copy } from "@phosphor-icons/react";
import TablePicker from "@/pages/checkin/TablePicker";
import ResultCard from "@/pages/checkin/ResultCard";

const METHODS = [
    { v: "cash", l: "Cash" },
    { v: "zelle", l: "Zelle" },
    { v: "venmo", l: "Venmo" },
];

const getVenueHandle = (method, venue) => {
    if (method === "zelle") return venue?.zelle || "";
    if (method === "venmo") return venue?.venmo || "";
    return "";
};

export default function CheckinPage() {
    const [tables, setTables] = useState([]);
    const [venue, setVenue] = useState({ zelle: "", venmo: "" });
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [amount, setAmount] = useState("");
    const [method, setMethod] = useState("cash");
    const [reference, setReference] = useState("");
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const load = useCallback(async () => {
        setTables(await PublicTables.list());
    }, []);

    useEffect(() => {
        load();
        Venue.payment().then(setVenue).catch(() => {});
        const timer = setInterval(load, 15000);
        return () => clearInterval(timer);
    }, [load]);

    const submit = async () => {
        if (!name.trim() || !phone.trim()) { toast.error("Enter your name and phone"); return; }
        if (!selected) { toast.error("Pick a table"); return; }
        setLoading(true);
        try {
            const r = await Checkin.submit({
                name: name.trim(),
                phone: phone.trim(),
                table_id: selected,
                intended_buyin: amount ? parseFloat(amount) : null,
                intended_method: method,
                intended_reference: reference || null,
            });
            setResult(r);
            if (r.status === "seat_available") toast.success("Seat available! Head to reception.");
            else if (r.status === "waitlisted") toast.info(`You are #${r.position} on the waitlist`);
            else toast.info("You are already on the waitlist");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Check-in failed");
        } finally { setLoading(false); }
    };

    const reset = () => {
        setName(""); setPhone(""); setAmount(""); setMethod("cash"); setReference("");
        setSelected(null); setResult(null);
    };

    const handle = getVenueHandle(method, venue);

    const copyHandle = async () => {
        if (!handle) return;
        try {
            await navigator.clipboard.writeText(handle);
            toast.success("Copied");
        } catch {
            toast.error("Unable to copy — please copy manually");
        }
    };

    const submitLabel = () => {
        if (loading) return "Checking in…";
        if (!selected) return "Pick a table first";
        return "Confirm check-in";
    };

    return (
        <div className="max-w-5xl mx-auto space-y-8" data-testid="checkin-page">
            <div className="text-center">
                <div className="inline-flex items-center gap-2 border gold-border rounded-full px-4 py-1 mb-4">
                    <Spade weight="fill" size={14} className="text-gold" />
                    <span className="label-mini">Player self check-in</span>
                </div>
                <h2 className="font-serif text-5xl tracking-tight">Welcome to the felt</h2>
                <p className="text-muted-foreground text-base mt-2 max-w-lg mx-auto">
                    Reserve your seat, pick your buy-in and payment method — we&#39;ll text you the moment a seat opens.
                </p>
            </div>

            {result ? (
                <ResultCard result={result} amount={amount} method={method} venue={venue} onReset={reset} />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                    <Card className="card-lux md:col-span-2">
                        <CardContent className="p-6 space-y-4">
                            <h3 className="font-serif text-2xl">Your info</h3>
                            <div>
                                <Label className="label-mini">Full name</Label>
                                <Input data-testid="ci-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jamie Rivera" />
                            </div>
                            <div>
                                <Label className="label-mini">Mobile (SMS-capable, E.164)</Label>
                                <Input data-testid="ci-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+15551234567" />
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    We&#39;ll only text you about your seat.
                                </p>
                            </div>

                            <div className="pt-2 border-t border-border/40">
                                <div className="label-mini mb-2">Buy-in intent</div>
                                <div className="grid grid-cols-3 gap-2 mb-3">
                                    {METHODS.map(o => (
                                        <button
                                            key={o.v}
                                            data-testid={`ci-method-${o.v}`}
                                            type="button"
                                            onClick={() => setMethod(o.v)}
                                            className={`h-10 rounded-md text-sm transition-colors border ${
                                                method === o.v
                                                    ? "border-gold bg-gold/15 text-gold"
                                                    : "border-border text-muted-foreground hover:text-foreground"
                                            }`}
                                        >
                                            {o.l}
                                        </button>
                                    ))}
                                </div>
                                <div>
                                    <Label className="label-mini">Amount ($)</Label>
                                    <Input data-testid="ci-amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="500" />
                                </div>
                                {method !== "cash" && handle && (
                                    <div className="mt-3 rounded-md border border-gold/30 bg-secondary/40 p-3 space-y-2" data-testid="ci-handle-box">
                                        <div className="label-mini">Transfer to</div>
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-mono-plex text-base text-gold truncate">{handle}</span>
                                            <button
                                                type="button"
                                                data-testid="ci-copy-handle"
                                                onClick={copyHandle}
                                                className="text-xs text-muted-foreground hover:text-foreground border border-border rounded px-2 py-1 flex items-center gap-1"
                                            >
                                                <Copy size={12} /> Copy
                                            </button>
                                        </div>
                                        <div>
                                            <Label className="label-mini">Your handle / confirmation (optional)</Label>
                                            <Input
                                                data-testid="ci-reference"
                                                value={reference}
                                                onChange={(e) => setReference(e.target.value)}
                                                placeholder={method === "zelle" ? "your@email or last 4" : "@your-venmo"}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <Button data-testid="ci-submit" disabled={loading || !selected} onClick={submit} className="btn-gold w-full h-11">
                                {submitLabel()}
                            </Button>
                        </CardContent>
                    </Card>

                    <div className="md:col-span-3">
                        <TablePicker tables={tables} selectedId={selected} onSelect={setSelected} />
                    </div>
                </div>
            )}
        </div>
    );
}
